// Authentication state management for d3-auth
// Supports local JWT auth and Entra ID SSO

import { PublicClientApplication } from '../vendor/msal.min.js';
import { getBackendUrl } from './backend.js';
import { consentsOutstandingFromToken } from './consents.js';
import { navigateAfterSignIn } from './pending-oauth.js';

const TOKEN_KEY = 'd3auth_access_token';
const REFRESH_KEY = 'd3auth_refresh_token';
const TOKEN_EXPIRY_KEY = 'd3auth_token_expiry';

class AuthStateManager {
    constructor() {
        /** @type {Set<Function>} */
        this.listeners = new Set();
        /** @type {object|null} */
        this.user = null;
        /** @type {boolean} */
        this.isAuthenticated = false;
        /** @type {boolean} */
        this.loading = true;
        /** @type {string[]} */
        this.permissions = [];
        /** @type {string|null} */
        this.tenantId = null;
        /** @type {string|null} - staff tenant override for admin operations */
        this.staffTenantId = null;
        /** @type {boolean} */
        this.sessionExpired = false;
        /** @type {boolean} - a required document is unacknowledged (consents_current claim) */
        this.consentsOutstanding = false;
        /** @type {ReturnType<typeof setTimeout>|null} */
        this._refreshTimer = null;
        /** @type {PublicClientApplication|null} */
        this._msalInstance = null;
        /** @type {object|null} */
        this._config = null;
    }

    subscribe(fn) {
        this.listeners.add(fn);
        return () => this.listeners.delete(fn);
    }

    notify() {
        const state = {
            isAuthenticated: this.isAuthenticated,
            user: this.user,
            permissions: this.permissions,
            loading: this.loading,
            sessionExpired: this.sessionExpired,
            tenantId: this.tenantId,
            staffTenantId: this.staffTenantId,
        };
        this.listeners.forEach((fn) => {
            fn(state);
        });
    }

    getState() {
        return {
            isAuthenticated: this.isAuthenticated,
            user: this.user,
            permissions: this.permissions,
            loading: this.loading,
            tenantId: this.tenantId,
            staffTenantId: this.staffTenantId,
        };
    }

    /**
     * Upstream sign-in providers this deployment has configured (from
     * /api/config). Empty until init() has run, so anything rendering a
     * provider button must re-render on the auth state change.
     * @returns {string[]}
     */
    getEnabledProviders() {
        return this._config?.providers || [];
    }

    getAccessToken() {
        return localStorage.getItem(TOKEN_KEY);
    }

    setTokens(tokens) {
        localStorage.setItem(TOKEN_KEY, tokens.access_token);
        localStorage.setItem(REFRESH_KEY, tokens.refresh_token);
        localStorage.setItem(TOKEN_EXPIRY_KEY, tokens.expires_at);
        this._scheduleRefresh(tokens.expires_at);
        this.sessionExpired = false;

        // Parse tenant_id from JWT
        try {
            const payload = JSON.parse(atob(tokens.access_token.split('.')[1]));
            this.tenantId = payload.tenant_id || null;
        } catch {
            this.tenantId = null;
        }

        // The consent gate reads the claim the token was just minted with, so
        // the sign-in redirect needs no extra round trip.
        this.consentsOutstanding = consentsOutstandingFromToken(tokens.access_token);
    }

    /** Set the staff tenant override for admin operations */
    setStaffTenant(tenantId) {
        this.staffTenantId = tenantId;
        this.notify();
    }

    /** Get headers for admin API calls, including X-Tenant-ID if staff override is set */
    getAdminHeaders() {
        const headers = { Authorization: `Bearer ${this.getAccessToken()}` };
        if (this.staffTenantId) {
            headers['X-Tenant-ID'] = this.staffTenantId;
        }
        return headers;
    }

    hasPermission(perm) {
        return this.permissions.includes(perm);
    }

    clearTokens() {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_KEY);
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
        this.consentsOutstanding = false;
        if (this._refreshTimer) {
            clearTimeout(this._refreshTimer);
            this._refreshTimer = null;
        }
    }

    async init() {
        this.loading = true;
        this.notify();

        // Fetch config and initialize MSAL
        try {
            const response = await fetch(`${getBackendUrl()}/api/config`);
            this._config = await response.json();

            this._msalInstance = new PublicClientApplication({
                auth: {
                    clientId: this._config.entraSpaClientId,
                    authority: `https://login.microsoftonline.com/${this._config.entraTenantId}`,
                    redirectUri: window.location.origin,
                    postLogoutRedirectUri: window.location.origin,
                },
                cache: {
                    cacheLocation: 'sessionStorage',
                    storeAuthStateInCookie: false,
                },
            });

            await this._msalInstance.initialize();

            // Handle Entra ID redirect callback
            const result = await this._msalInstance.handleRedirectPromise();
            console.log('MSAL handleRedirectPromise result:', result);

            if (result?.account) {
                console.log('Entra login: exchanging token for d3-auth session...');
                // Exchange Entra ID token for d3-auth JWT
                const exchangeResponse = await fetch(`${getBackendUrl()}/api/v1/entra-login`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        access_token: result.accessToken,
                    }),
                });

                const data = await exchangeResponse.json();
                console.log('Entra login exchange response:', exchangeResponse.status, data);

                if (!exchangeResponse.ok) {
                    console.error('Entra login exchange failed:', data);
                    this.loading = false;
                    this.notify();
                    return;
                }

                if (data.setup_required) {
                    sessionStorage.setItem('d3auth_setup_email', data.email);
                    sessionStorage.setItem('d3auth_setup_token', data.setup_token);
                    if (data.name) sessionStorage.setItem('d3auth_setup_name', data.name);
                    this.loading = false;
                    this.notify();
                    window.history.replaceState({}, document.title, '/setup');
                    return;
                }

                this.setTokens(data.tokens);
                this.isAuthenticated = true;
                await this.fetchCurrentUser();
                this.loading = false;
                this.notify();

                // Back to wherever this sign-in belongs: an app waiting on an
                // authorize request, or the profile page. Staying put is also an
                // answer — the OAuth and device-verify views resume themselves.
                navigateAfterSignIn(sessionStorage, this.consentsOutstanding);
                return;
            }
        } catch (err) {
            console.error('MSAL error:', err);
        }

        // Check for existing local JWT session
        const token = this.getAccessToken();
        if (token) {
            // If the token has expired, try refreshing before giving up
            const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
            const isExpired = expiry && new Date(expiry).getTime() < Date.now();

            if (isExpired) {
                await this._refreshTokens();
            }

            try {
                await this.fetchCurrentUser();
                this.isAuthenticated = true;
                const newExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
                if (newExpiry) this._scheduleRefresh(newExpiry);
            } catch {
                // Clear the dead session, but leave the URL alone. Rewriting it
                // here threw away the path the user actually asked for — an
                // invitation link became "/" and then reported itself invalid
                // (#73). replaceState does not notify the router either, so it
                // only ever desynced the address bar from the mounted view.
                this.clearTokens();
                this.isAuthenticated = false;
                this.sessionExpired = true;
            }
        }

        this.loading = false;
        this.notify();
    }

    async fetchCurrentUser() {
        const token = this.getAccessToken();
        if (!token) throw new Error('No access token');

        const response = await fetch(`${getBackendUrl()}/api/v1/me`, {
            headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch user: ${response.statusText}`);
        }

        const data = await response.json();
        this.user = data.user;
        this.permissions = data.permissions || [];
    }

    async loginWithEntra(email) {
        if (!this._msalInstance) {
            throw new Error('Single sign-on is not available');
        }

        await this._msalInstance.loginRedirect({
            scopes: ['api://auth.dicedriven.com/access:user'],
            loginHint: email,
        });
    }

    async logout() {
        const token = this.getAccessToken();
        if (token) {
            try {
                await fetch(`${getBackendUrl()}/api/v1/logout`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                });
            } catch {
                // Ignore logout API errors
            }
        }

        this.clearTokens();
        this.user = null;
        this.isAuthenticated = false;
        this.notify();
    }

    async _refreshTokens() {
        const refreshToken = localStorage.getItem(REFRESH_KEY);
        if (!refreshToken) return;

        try {
            const response = await fetch(`${getBackendUrl()}/api/v1/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh_token: refreshToken }),
            });

            if (!response.ok) {
                // As in init(): clear the session, leave the URL alone (#73).
                // This path fires mid-session on any page, so a rewrite here
                // could discard whatever the user was in the middle of.
                this.clearTokens();
                this.user = null;
                this.isAuthenticated = false;
                this.sessionExpired = true;
                this.notify();
                return;
            }

            const data = await response.json();
            this.setTokens(data.tokens);
        } catch {
            // Network error — retry in 10 seconds
            console.warn('Token refresh failed (network error), retrying in 10s...');
            setTimeout(() => this._refreshTokens(), 10000);
        }
    }

    _scheduleRefresh(expiresAt) {
        if (this._refreshTimer) {
            clearTimeout(this._refreshTimer);
        }

        const expiryTime = new Date(expiresAt).getTime();
        const now = Date.now();
        const refreshIn = Math.max(0, expiryTime - now - 60000);

        this._refreshTimer = setTimeout(() => this._refreshTokens(), refreshIn);
    }
}

export const authState = new AuthStateManager();
